import { useState } from "react";
import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface AvatarProps {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdAt: string;
  updatedAt: string;
}

interface AvatarUpdateProps {
  avatar: AvatarProps;
}

// #STYLE_GUIDE: Update component for editing existing records (includes delete functionality)
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
// #TODO: This is a basic implementation - customize form fields and validation as needed
export default function AvatarUpdate({ avatar }: AvatarUpdateProps) {
  const [formData, setFormData] = useState({
    name: avatar.name || "",
  });


  const { loading, errors, makeRequest } = useApiRequest({
    onSuccess: (data) => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit(`/avatars/${data.id}`);
    },
    onError: (error) => {
      console.error("Failed to update avatar:", error);
    },
  });

  const { loading: deleteLoading, makeRequest: makeDeleteRequest } = useApiRequest({
    onSuccess: () => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit("/avatars");
    },
    onError: (error) => {
      console.error("Failed to delete avatar:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
    };

    await makeRequest("PUT", `/api/v1/avatars/${avatar.id}`, {
      avatar: submitData,
    });
  };

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this avatar? This action cannot be undone.")) {
      await makeDeleteRequest("DELETE", `/api/v1/avatars/${avatar.id}`);
    }
  };

  const handleCancel = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/avatars/${avatar.id}`);
  };

  const getFieldError = (fieldName: string) => {
    return errors?.find((error: any) => error.field === fieldName)?.message;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Edit Avatar</h1>
        <div className="join">
          <button
            onClick={handleCancel}
            className="btn btn-outline join-item"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn btn-error join-item"
            disabled={deleteLoading || loading}
          >
            {deleteLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Name</span>
              </label>
              <input
                type="text"
                className={`input input-bordered ${getFieldError('name') ? 'input-error' : ''}`}
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  name: e.target.value
                }))}
              />
              {getFieldError('name') && (
                <label className="label">
                  <span className="label-text-alt text-error">{getFieldError('name')}</span>
                </label>
              )}
            </div>

            {/* Submit buttons */}
            <div className="form-control mt-6">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading || deleteLoading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Updating...
                    </>
                  ) : (
                    "Update Avatar"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  disabled={loading || deleteLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
